import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { PromptTab } from '../bughunt/SessionPanels'
import { DeliveryCard } from '../components/DeliveryCard'
import { SessionLog } from '../components/SessionLog'
import { ArrowLeft, Check, CircleDot, GitBranch, GitCommitHorizontal, Info, Minus, Share2, X } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatCost, formatDuration, formatNumber, formatTime } from '../lib/format'
import { realtime } from '../lib/realtime'
import { Avatar, Badge, EmptyState, Panel, ProgressBar, Segmented, Skeleton, Stat, toneForState } from '../components/primitives'
import type { TimelineEntry, WorkSessionDetail } from '../lib/types'

/**
 * How often the detail payload is refetched while events are arriving, in ms.
 *
 * A busy agent emits several events a second. The header can follow every one of them because the
 * broadcast carries it; everything else has to come from the server, and pulling the whole payload
 * per event would be wasteful without being any more truthful to a reader.
 */
const DETAIL_REFETCH_MS = 2000

export default function WorkSession() {
  const { id = '' } = useParams()
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()

  const [sharing, setSharing] = useState(false)
  // Conversa or Detalhes (every event the AI reported, with its payload) — the owner's ask, 16/10.
  const [talk, setTalk] = useState<'talk' | 'details'>('talk')
  const { data, isLoading, isError } = useQuery({
    queryKey: ['work', id],
    queryFn: () => api.sessions.detail(id),
    enabled: Boolean(id),
  })

  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (refetchTimer.current !== null) clearTimeout(refetchTimer.current)
    },
    [],
  )

  // The detail view is the only screen that wants every event for a session, so it opts into that
  // group explicitly rather than the hub pushing the firehose to everyone on the floor.
  useEffect(() => {
    if (!id) return
    void realtime.watchSession(id)
    return () => {
      void realtime.unwatchSession(id)
    }
  }, [id])

  useEffect(
    () =>
      realtime.onTimeline(({ sessionId, entry }) => {
        if (sessionId !== id) return

        queryClient.setQueryData<WorkSessionDetail>(['work', id], (current) => {
          if (!current) return current
          // Guard against a duplicate arriving from a retry: the row is keyed by the agent's event
          // id, and appending it twice would show the same line twice.
          if (current.timeline.some((e) => e.eventId === entry.eventId)) return current
          return { ...current, timeline: [...current.timeline, entry] }
        })
      }),
    [id, queryClient],
  )

  useEffect(
    () =>
      realtime.onSessions((updates) => {
        const update = updates.find((u) => u.workSessionId === id)
        if (!update) return

        queryClient.setQueryData<WorkSessionDetail>(['work', id], (current) =>
          current ? { ...current, session: update } : current,
        )

        // Plan and aggregate panels are refetched rather than patched: they are derived from rows
        // the broadcast does not carry, and inventing them client-side would let the two disagree.
        //
        // This once passed `refetchType: 'none'`, which marks a query stale and then never
        // refetches it — not even for a mounted observer. So the header followed the session all
        // the way to Completed and 100% while the work plan under it stayed frozen at whatever it
        // held when the page opened, showing a finished session with two steps still running. The
        // answer is to throttle the refetch, not to suppress it.
        if (refetchTimer.current !== null) return

        refetchTimer.current = setTimeout(() => {
          refetchTimer.current = null
          void queryClient.invalidateQueries({ queryKey: ['work', id], exact: true })
        }, DETAIL_REFETCH_MS)
      }),
    [id, queryClient],
  )

  if (isLoading) return <div className="stack"><Skeleton height={120} /><Skeleton height={300} /></div>
  if (isError || !data) return <EmptyState title={t('work.notFound')} />

  const { session, plan, timeline, codeChanges, testRuns, documents, gitActivities, aiUsage, blockers } = data
  const openBlockers = blockers.filter((b) => b.status === 'Open')

  return (
    <div className="stack stack--loose work-page">
      <div className="row row--between">
        <Link to="/track/floor" className="btn btn--ghost btn--sm">
          <ArrowLeft size={14} />
          {t('live.title')}
        </Link>
        {/* The delivery as an image to paste anywhere: metadata only (41 §4.2). */}
        <button type="button" className="btn btn--sm" onClick={() => setSharing(true)}>
          <Share2 size={13} /> {t('share.button')}
        </button>
      </div>
      {sharing && <DeliveryCard detail={data} onClose={() => setSharing(false)} />}

      <Panel>
        {/* On a phone the progress goes under the title, full width, instead of squeezing beside it. */}
        <div className="row work-head" style={{ alignItems: 'flex-start' }}>
          <Avatar name={session.developerName} color={session.avatarColor} size="lg" />

          <div className="stack stack--tight" style={{ flex: 1, minWidth: 0 }}>
            <div className="row row--tight row--wrap">
              {session.taskExternalId && <span className="mono bold">{session.taskExternalId}</span>}
              <Badge tone={toneForState(session.activityState)}>{t(`sessionStatus.${session.status}`)}</Badge>
              {session.taskType && <Badge>{t(`taskType.${session.taskType}`)}</Badge>}
              {session.isSimulated && <Badge tone="demo">{t('common.demoData')}</Badge>}
            </div>

            {/* Work with no task (a prompt, a command from the phone) is named by where it happened. */}
            <h2>{session.taskTitle ?? (session.repositoryName ? t('work.untitledIn', { repository: session.repositoryName }) : t('work.untitled'))}</h2>

            <div className="row row--wrap tiny muted" style={{ gap: 'var(--s4)' }}>
              <span>{session.developerName}</span>
              {session.repositoryName && <span className="mono">{session.repositoryName}</span>}
              {session.branch && (
                <span className="row row--tight">
                  <GitBranch size={11} />
                  <span className="mono">{session.branch}</span>
                </span>
              )}
              {data.spec && (
                <Link to={`/project/specs?spec=${data.spec.id}`} className="mono" title={data.spec.title}>{data.spec.key}</Link>
              )}
              {session.providerName && <span>{session.providerName}</span>}
              {session.modelName && <span>{session.modelName}</span>}
            </div>
          </div>

          <div className="stack stack--tight work-head__progress">
            <ProgressBar
              value={session.progressPercent}
              showValue
              size="lg"
              tone={session.activityState === 'Blocked' ? 'danger' : session.activityState === 'Completed' ? 'ok' : undefined}
              label={t('work.progress')}
            />
            <div className="row row--between tiny muted">
              <span>{t('work.duration')}</span>
              <span>{formatDuration(session.durationSeconds, locale)}</span>
            </div>
            {session.currentStepTitle && (
              <div className="row row--between tiny">
                <span className="muted">{t('live.currentStep')}</span>
                <span className="truncate" style={{ maxWidth: 140 }}>{session.currentStepTitle}</span>
              </div>
            )}
          </div>
        </div>
      </Panel>

      {openBlockers.length > 0 && (
        <Panel title={t('work.blockers')}>
          <div className="stack stack--tight">
            {openBlockers.map((blocker) => (
              <div key={blocker.id} className="row row--tight">
                <Badge tone="danger">{blocker.category ?? t('event.blocker.detected')}</Badge>
                <span>{blocker.description}</span>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <div className="grid grid--stats">
        <Stat
          label={t('work.filesChanged')}
          value={formatNumber(codeChanges.filesChanged, locale)}
          foot={
            <span title={t(`work.metricSourceHint.${codeChanges.source}`)} className="row row--tight">
              <Info size={10} />
              {t(`work.metricSource.${codeChanges.source}`)}
            </span>
          }
        />
        <Stat label={t('work.linesAdded')} value={`+${formatNumber(codeChanges.linesAdded, locale)}`} tone="ok" />
        <Stat label={t('work.linesDeleted')} value={`−${formatNumber(codeChanges.linesDeleted, locale)}`} tone="danger" />
        <Stat
          label={t('work.tests')}
          value={session.testsTotal > 0 ? `${session.testsPassed}/${session.testsTotal}` : '—'}
          tone={session.testsTotal > 0 && session.testsPassed < session.testsTotal ? 'warn' : 'ok'}
        />
        <Stat
          label={t('reports.tokens')}
          value={formatNumber(aiUsage.inputTokens + aiUsage.outputTokens, locale)}
          foot={aiUsage.cachedInputTokens > 0 ? `${formatNumber(aiUsage.cachedInputTokens, locale)} ${t('work.cachedTokens').toLowerCase()}` : undefined}
        />
        <Stat
          label={t('live.cost')}
          value={session.providerSlug === 'opencode' ? formatCost(0, locale) : aiUsage.accuracy === 'Unavailable' ? '—' : formatCost(aiUsage.costUsd, locale)}
          foot={session.providerSlug === 'opencode' ? t('work.localCost') : t(`cost.${aiUsage.accuracy}`)}
          tone={session.providerSlug === 'opencode' || aiUsage.accuracy !== 'Unavailable' ? 'accent' : undefined}
        />
      </div>

      {/* What was asked and what the agent answered, when kept (or the person keeps their own, a week). */}
      <div className="work-page__conversation">
        <Panel
          title={t('work.conversation')}
          actions={
            <Segmented<'talk' | 'details'>
              value={talk}
              onChange={setTalk}
              options={[
                { value: 'talk', label: t('work.tab.talk') },
                { value: 'details', label: t('work.tab.details', { count: timeline.length }) },
              ]}
            />
          }
        >
          {talk === 'talk' ? <PromptTab workSessionId={session.workSessionId} /> : <SessionLog timeline={timeline} />}
        </Panel>
      </div>

      {/* On a phone, what the agent is doing comes right under the header: the timeline, then the plan. */}
      <div className="grid grid--two work-page__live">
        <Panel title={t('work.plan')} flush>
          {!plan || plan.steps.length === 0 ? (
            <EmptyState title={t('work.noPlan')} body={t('work.noPlanHint')} />
          ) : (
            <div className="stack stack--tight" style={{ padding: 'var(--s4)' }}>
              {plan.steps.map((step) => (
                <div key={step.id} className="row row--tight">
                  <StepIcon status={step.status} />
                  <div className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
                    <div className="row row--between">
                      <span className={step.status === 'Completed' ? 'muted truncate' : 'truncate'}>{step.title}</span>
                      <span className="tiny muted nowrap">{t('work.stepWeight', { weight: step.weight })}</span>
                    </div>
                    {step.status === 'InProgress' && <ProgressBar value={step.progressPercent} size="sm" />}
                    {step.note && <span className="tiny muted truncate">{step.note}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title={t('work.timeline')} flush>
          <div className="timeline" style={{ maxHeight: 460, overflowY: 'auto' }}>
            {timeline.map((entry) => (
              <TimelineRow key={entry.id} entry={entry} />
            ))}
          </div>
        </Panel>
      </div>

      {(documents.length > 0 || gitActivities.length > 0 || testRuns.length > 0) && (
        <div className="grid grid--two">
          {testRuns.length > 0 && (
            <Panel title={t('work.tests')} flush>
              <table className="table">
                <tbody>
                  {testRuns.map((run) => (
                    <tr key={run.id}>
                      <td className="mono tiny">{run.framework ?? '—'}</td>
                      <td className="truncate">{run.suite ?? '—'}</td>
                      <td className="right nowrap">
                        <Badge tone={run.failed > 0 ? 'danger' : 'ok'}>
                          {run.passed}/{run.total}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}

          {documents.length > 0 && (
            <Panel title={t('work.documents')} flush>
              <table className="table">
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td className="mono tiny truncate">{doc.documentPath}</td>
                      <td><Badge>{doc.category}</Badge></td>
                      <td className="right tiny muted">
                        {doc.completenessPercent !== null ? `${doc.completenessPercent}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}

          {gitActivities.length > 0 && (
            <Panel title={t('work.git')} flush>
              <table className="table">
                <tbody>
                  {gitActivities.map((activity) => (
                    <tr key={activity.id}>
                      <td style={{ width: 20 }}><GitCommitHorizontal size={13} /></td>
                      <td className="mono tiny">{activity.commitSha?.slice(0, 7) ?? activity.activityType}</td>
                      <td className="truncate">{activity.message ?? activity.pullRequestNumber ?? '—'}</td>
                      <td className="right tiny muted nowrap">{formatTime(activity.occurredAt, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}
        </div>
      )}
    </div>
  )
}

function StepIcon({ status }: { status: string }) {
  if (status === 'Completed') return <Check size={14} color="var(--ok)" />
  if (status === 'Failed') return <X size={14} color="var(--danger)" />
  if (status === 'Skipped') return <Minus size={14} color="var(--text-muted)" />
  if (status === 'InProgress') return <CircleDot size={14} color="var(--accent)" />
  return <CircleDot size={14} color="var(--text-muted)" opacity={0.4} />
}

function TimelineRow({ entry }: { entry: TimelineEntry }) {
  const { t, locale } = useI18n()

  const tone = (() => {
    if (entry.eventType === 'blocker.detected' || entry.eventType === 'work.failed' || entry.eventType === 'step.failed') return 'var(--danger)'
    if (entry.eventType === 'work.completed' || entry.eventType === 'step.completed' || entry.eventType === 'blocker.resolved') return 'var(--ok)'
    if (entry.eventType.startsWith('test.')) return 'var(--warn)'
    if (entry.eventType.startsWith('git.')) return 'var(--purple)'
    return 'var(--text-muted)'
  })()

  return (
    <div className="timeline__row">
      <span className="timeline__time">{formatTime(entry.occurredAt, locale)}</span>
      <span className="timeline__marker">
        <span className="timeline__dot" style={{ background: tone }} />
      </span>
      <span className="timeline__text">
        {entry.summary ?? t(`event.${entry.eventType}`)}
        <span className="timeline__type"> · {t(`event.${entry.eventType}`)}</span>
      </span>
    </div>
  )
}
